import { Eye, Layout } from 'lucide-react';
import { useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { FormPreview } from '../preview/form-preview';
import { fieldRegistry } from '../registry';
import type { FormFieldSchema } from '../types';
import { BuilderArea } from './builder-area';
import { FieldConfigModal } from './field-config-modal';
import { FieldTypePanel } from './field-type-panel';

const DEFAULT_CUSTOMER_FIELDS: FormFieldSchema[] = [];

export function CustomFieldsBuilder({
  initialFields = DEFAULT_CUSTOMER_FIELDS,
  onSave,
  isSaving = false,
  label: initialLabel = 'New Contact Type',
}: {
  initialFields?: FormFieldSchema[];
  onSave?: (label: string, fields: FormFieldSchema[]) => void;
  isSaving?: boolean;
  label?: string;
}) {
  const [fields, setFields] = useState<FormFieldSchema[]>(initialFields);
  const [label, setLabel] = useState(initialLabel);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleFieldDrop = (fieldType: string) => {
    const id = `field-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
    const definition = fieldRegistry.get(fieldType);

    if (!definition) return;

    const newField: FormFieldSchema = definition.createDefault(id);
    setFields([...fields, newField]);
  };

  const handleEditField = (fieldId: string) => {
    setEditingFieldId(fieldId);
    setIsModalOpen(true);
  };

  const handleDeleteField = (fieldId: string) => {
    setFields(fields.filter((f) => f.id !== fieldId));
  };

  const handleReorderFields = (fromIndex: number, toIndex: number) => {
    const newFields = [...fields];
    const [movedField] = newFields.splice(fromIndex, 1);
    newFields.splice(toIndex, 0, movedField);
    setFields(newFields);
  };

  const handleSaveField = (updatedField: FormFieldSchema) => {
    setFields(fields.map((f) => (f.id === updatedField.id ? updatedField : f)));
    setEditingFieldId(null);
    setIsModalOpen(false);
  };

  const editingField = fields.find((f) => f.id === editingFieldId);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="build" className="space-y-6">
            <div className="flex items-center justify-between bg-card p-4 rounded-xl border border-border shadow-sm">
              <div className="flex items-center gap-4 flex-1">
                <div className="space-y-1">
                  <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="text-2xl font-bold bg-transparent border-none focus:ring-0 w-full"
                    placeholder="Contact Type Name"
                  />
                  <p className="text-sm text-muted-foreground">
                    Design your custom fields for this contact type
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <TabsList className="grid w-[200px] grid-cols-2">
                  <TabsTrigger value="build" className="gap-2">
                    <Layout className="h-4 w-4" />
                    Build
                  </TabsTrigger>
                  <TabsTrigger value="preview" className="gap-2">
                    <Eye className="h-4 w-4" />
                    Preview
                  </TabsTrigger>
                </TabsList>
                <Button
                  onClick={() => {
                    // Auto-assign slots to fields that don't have them
                    const usedSlots = new Set(fields.map((f) => f.slot).filter(Boolean));
                    const nextAvailableSlot = () => {
                      for (let i = 1; i <= 20; i++) {
                        const slotName = `filterSlot${i}`;
                        if (!usedSlots.has(slotName)) {
                          usedSlots.add(slotName);
                          return slotName;
                        }
                      }
                      return undefined;
                    };

                    const fieldsWithSlots = fields.map((field) => {
                      if (field.slot) return field;
                      return { ...field, slot: nextAvailableSlot() };
                    });

                    onSave?.(label, fieldsWithSlots);
                  }}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Schema'}
                </Button>
              </div>
            </div>

            <TabsContent value="build" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Left: Field Types Panel */}
                <div className="lg:col-span-1 bg-card rounded-xl shadow-sm border border-border h-fit">
                  <FieldTypePanel onFieldDrop={handleFieldDrop} />
                </div>

                {/* Center: Builder Area */}
                <div className="lg:col-span-3 bg-card rounded-xl shadow-sm border-2 border-dashed border-border hover:border-primary/50 transition-colors min-h-[600px]">
                  <BuilderArea
                    fields={fields}
                    onEditField={handleEditField}
                    onDeleteField={handleDeleteField}
                    onReorderFields={handleReorderFields}
                    onAddField={handleFieldDrop}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="preview" className="mt-0">
              <div className="max-w-2xl mx-auto bg-card rounded-xl shadow-sm border border-border min-h-[500px]">
                <FormPreview fields={fields} />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Field Configuration Modal */}
        {editingField && (
          <FieldConfigModal
            isOpen={isModalOpen}
            field={editingField}
            onClose={() => {
              setIsModalOpen(false);
              setEditingFieldId(null);
            }}
            onSave={handleSaveField}
          />
        )}
      </div>
    </DndProvider>
  );
}
